import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { ConversationStatus } from '@/api/types';

export type AssignedToFilter = 'me' | 'unassigned' | 'all';

export interface InboxFilters {
  status: ConversationStatus[];
  assignedTo: AssignedToFilter;
}

const DEFAULT_FILTERS: InboxFilters = {
  status: ['open', 'pending'],
  assignedTo: 'me',
};

/**
 * Hook to manage inbox filters synced with URL search params
 */
export function useInboxFilters() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Parse filters from URL
  const filters: InboxFilters = useMemo(() => {
    const statusParam = searchParams.get('status');
    const assignedToParam = searchParams.get('assigned') as AssignedToFilter | null;

    return {
      status: statusParam
        ? (statusParam.split(',') as ConversationStatus[])
        : DEFAULT_FILTERS.status,
      assignedTo: assignedToParam || DEFAULT_FILTERS.assignedTo,
    };
  }, [searchParams]);

  // Update filters in URL
  const setFilters = useCallback(
    (updates: Partial<InboxFilters>) => {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);

        if (updates.status !== undefined) {
          if (updates.status.length > 0) {
            newParams.set('status', updates.status.join(','));
          } else {
            newParams.delete('status');
          }
        }

        if (updates.assignedTo !== undefined) {
          if (updates.assignedTo !== 'all') {
            newParams.set('assigned', updates.assignedTo);
          } else {
            newParams.delete('assigned');
          }
        }

        return newParams;
      });
    },
    [setSearchParams]
  );

  const resetFilters = useCallback(() => {
    setSearchParams({});
  }, [setSearchParams]);

  return { filters, setFilters, resetFilters };
}
